/**
 * Selectable tint colors for an icon in the `IconPicker`, in swatch order.
 *
 * Each name maps to a Popcorn color family and drives three tints via the CSS
 * modifier classes: the solid swatch fill (`-400`), the icon ink (`-500`), and
 * the selected-icon background tile (`-a-200`). `greenDark` is the one non-family
 * entry: it reuses the green palette's darker `-600` shade so the picker can
 * offer the two greens shown in the design.
 */
export const ICON_COLORS = [
  'gray',
  'yellow',
  'orange',
  'red',
  'pink',
  'purple',
  'blue',
  'teal',
  'green',
  'greenDark',
] as const

export type IconColorName = (typeof ICON_COLORS)[number]

const ICON_COLOR_SET: ReadonlySet<string> = new Set(ICON_COLORS)

/**
 * Narrows an arbitrary string to an {@link IconColorName}. Handy at boundaries
 * such as a persisted tint that may name a color no longer offered.
 */
export function isIconColorName(value: string): value is IconColorName {
  return ICON_COLOR_SET.has(value)
}

export const DEFAULT_ICON_COLOR: IconColorName = 'gray'

/**
 * Human-readable swatch labels for assistive tech. Keeps the raw token names
 * (e.g. `greenDark`) out of the accessible name a screen reader announces.
 */
export const ICON_COLOR_LABELS: Record<IconColorName, string> = {
  gray: 'Gray',
  yellow: 'Yellow',
  orange: 'Orange',
  red: 'Red',
  pink: 'Pink',
  purple: 'Purple',
  blue: 'Blue',
  teal: 'Teal',
  green: 'Green',
  greenDark: 'Dark green',
}
